export const APPEARANCE_KEY = "prepnexis.admin.appearance.v1";
export const DEFAULT_COLORS = { accent: "#17806b", sidebar: "#142e25", background: "#f6f8f6", surface: "#ffffff" };
export const PRESETS = [
  { name: "Forest", colors: DEFAULT_COLORS },
  { name: "Ocean", colors: { accent: "#2563eb", sidebar: "#152b4d", background: "#f3f6fc", surface: "#ffffff" } },
  { name: "Violet", colors: { accent: "#7c3aed", sidebar: "#2c1d45", background: "#f7f4fc", surface: "#ffffff" } },
  { name: "Rose", colors: { accent: "#be185d", sidebar: "#421c30", background: "#fcf4f7", surface: "#ffffff" } },
  { name: "Sunset", colors: { accent: "#c2410c", sidebar: "#3b251d", background: "#fcf6f0", surface: "#ffffff" } },
  { name: "Midnight", colors: { accent: "#60a5fa", sidebar: "#0f172a", background: "#111827", surface: "#1e293b" } },
];

export function normalizeHex(value) {
  if (typeof value !== "string") return null;
  const hex = value.trim();
  if (/^#[0-9a-f]{6}$/i.test(hex)) return hex.toLowerCase();
  if (/^#[0-9a-f]{3}$/i.test(hex)) return `#${hex.slice(1).split("").map((part) => part + part).join("")}`.toLowerCase();
  return null;
}
export const sanitizeColors = (value) => Object.fromEntries(Object.entries(DEFAULT_COLORS).map(([key, fallback]) => [key, normalizeHex(value?.[key]) || fallback]));
const rgb = (hex) => [1, 3, 5].map((index) => parseInt(hex.slice(index, index + 2), 16));
export function mix(first, second, weight) {
  const other = rgb(second);
  return `#${rgb(first).map((value, index) => Math.round(value * weight + other[index] * (1 - weight)).toString(16).padStart(2, "0")).join("")}`;
}
const luminance = (hex) => rgb(hex).map((value) => value / 255).map((value) => value <= .04045 ? value / 12.92 : ((value + .055) / 1.055) ** 2.4).reduce((sum, value, index) => sum + value * [.2126, .7152, .0722][index], 0);
export function contrast(first, second) {
  const a = luminance(first), b = luminance(second);
  return (Math.max(a, b) + .05) / (Math.min(a, b) + .05);
}
export const foreground = (background) => {
  const white = contrast("#ffffff", background), dark = contrast("#111827", background);
  if (Math.max(white, dark) < 4.5) return "#000000";
  return white > dark ? "#ffffff" : "#111827";
};
export function readable(color, background) {
  const ink = foreground(background);
  for (let step = 0; step <= 20; step += 1) {
    const candidate = mix(ink, color, step / 20);
    if (contrast(candidate, background) >= 4.5) return candidate;
  }
  return ink;
}
export function appearanceVariables(input) {
  const colors = sanitizeColors(input);
  const pageText = foreground(colors.background), cardText = foreground(colors.surface), sidebarText = foreground(colors.sidebar);
  const soft = mix(cardText, colors.surface, .045);
  const accentSoft = mix(colors.accent, colors.surface, .12);
  return {
    "--appearance-accent": colors.accent,
    "--appearance-on-accent": foreground(colors.accent),
    "--appearance-accent-hover": mix(foreground(colors.accent), colors.accent, .12),
    "--appearance-sidebar": colors.sidebar,
    "--appearance-sidebar-text": sidebarText,
    "--appearance-sidebar-muted": readable(mix(sidebarText, colors.sidebar, .7), colors.sidebar),
    "--appearance-sidebar-border": mix(sidebarText, colors.sidebar, .2),
    "--appearance-sidebar-hover": mix(sidebarText, colors.sidebar, .09),
    "--appearance-background": colors.background,
    "--appearance-surface": colors.surface,
    "--appearance-page-text": pageText,
    "--appearance-page-muted": readable(mix(pageText, colors.background, .62), colors.background),
    "--appearance-page-border": mix(pageText, colors.background, .15),
    "--appearance-card-text": cardText,
    "--appearance-card-muted": readable(mix(cardText, colors.surface, .62), colors.surface),
    "--appearance-card-border": mix(cardText, colors.surface, .15),
    "--appearance-card-soft": soft,
    "--appearance-accent-soft": accentSoft,
    "--appearance-on-soft": readable(colors.accent, accentSoft),
    "--appearance-accent-page": readable(colors.accent, colors.background),
    "--appearance-accent-card": readable(colors.accent, colors.surface),
    "--appearance-chart": readable(colors.accent, colors.surface),
  };
}
